import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { apiClient } from 'config/api';

interface UseDownloadReceiptByTransactionIdResponse {
  isLoading: boolean;
  downloadReceipt: (transactionId: string) => Promise<void>;
  error: string | undefined;
}

/**
 * Custom hook to download a receipt by transaction ID
 * @returns { isLoading, downloadReceipt, error }
 */
const useDownloadReceipt = (): UseDownloadReceiptByTransactionIdResponse => {
  const { t } = useTranslation();
  const [isLoading, setIsloading] = useState(false);
  const [error, setError] = useState<string>();

  const downloadReceipt = useCallback(
    async (transactionId: string) => {
      setIsloading(true);
      setError(undefined);
      try {
        const { headers, data } = await apiClient.get(`/s3/receipt/${transactionId}`, {
          responseType: 'blob',
        });

        /**
         * Get the value of a header from the headers object
         * @param key - The key to look for in the headers object
         */
        const getHeaderValue = (key: string) => {
          const originalKey = Object.keys(headers).find(
            (k) => k.toLowerCase() === key.toLowerCase()
          );
          return originalKey ? headers[originalKey] : undefined;
        };

        const contentDisposition = getHeaderValue('content-disposition');
        const contentType = getHeaderValue('content-type');
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = t('CONSUMPTION:FILE_NAME', {
          fileName: filenameMatch[1],
        });

        const blob = new Blob([data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      } catch (error) {
        if (isAxiosError(error)) {
          setError(error.message);
        }
      } finally {
        setIsloading(false);
      }
    },
    [t]
  );

  return { isLoading, downloadReceipt, error };
};

export default useDownloadReceipt;
